import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  console.log('useSubscription: Hook called');
  const context = useContext(SubscriptionContext);
  console.log('useSubscription: Context value:', context);
  if (!context) {
    console.error('useSubscription: Context is null/undefined');
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  console.log('useSubscription: Returning context:', context);
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  console.log('SubscriptionProvider: Initializing provider...');
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();

  // Load subscription data from localStorage or backend when authentication state changes
  useEffect(() => {
    async function initializeSubscription() {
      console.log('SubscriptionProvider: Initializing...', { isAuthenticated, user });
      
      // Only proceed if user is authenticated
      if (!isAuthenticated || !user) {
        console.log('SubscriptionProvider: User not authenticated, skipping initialization');
        setSubscription(null);
        setPlans([]);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      
      // Wait a bit to ensure token is available
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Clear any old/invalid subscription data to start fresh
      const savedSubscription = localStorage.getItem('subscription');
      const savedPlans = localStorage.getItem('plans');
      console.log('SubscriptionProvider: Saved subscription:', savedSubscription);
      console.log('SubscriptionProvider: Saved plans:', savedPlans);
      
      // If saved data exists, validate it first
      if (savedSubscription || savedPlans) {
        try {
          const parsedSubscription = savedSubscription ? JSON.parse(savedSubscription) : null;
          const parsedPlans = savedPlans ? JSON.parse(savedPlans) : [];
          
          // Validate the data
          if (parsedSubscription !== null && typeof parsedSubscription === 'object') {
            setSubscription(parsedSubscription);
          }
          
          if (Array.isArray(parsedPlans)) {
            setPlans(parsedPlans);
          }
          
          // If we have valid data, we're done
          if ((parsedSubscription !== null || parsedPlans.length > 0)) {
            setLoading(false);
            console.log('SubscriptionProvider: Loaded from localStorage');
            return;
          }
        } catch (e) {
          console.log('SubscriptionProvider: Clearing corrupted subscription data');
          localStorage.removeItem('subscription');
          localStorage.removeItem('plans');
        }
      }
      
      const token = localStorage.getItem('token');
      
      // Always fetch from backend when user is authenticated to ensure fresh data
      if (token) {
        try {
          console.log('SubscriptionProvider: Fetching fresh data from backend...');
          const result = await import('../api/cacheAfterLogin').then((mod) => mod.cacheAfterLogin());
          console.log('SubscriptionProvider: Cache result:', result);
          
          const subscriptionData = result.subscription || null;
          const plansData = result.plans || [];
          
          console.log('SubscriptionProvider: Setting subscription data:', subscriptionData);
          console.log('SubscriptionProvider: Setting plans data:', plansData);
          
          setSubscription(subscriptionData);
          setPlans(plansData);
          
          console.log('SubscriptionProvider: Successfully loaded from backend');
        } catch (err) {
          console.error('SubscriptionProvider: Error loading subscription from backend:', err);
          console.error('SubscriptionProvider: Error details:', err.message, err.stack);
          
          // Retry once after a short delay
          try {
            console.log('SubscriptionProvider: Retrying after delay...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            const result = await import('../api/cacheAfterLogin').then((mod) => mod.cacheAfterLogin());
            const subscriptionData = result.subscription || null;
            const plansData = result.plans || [];
            console.log('SubscriptionProvider: Retry successful:', { subscriptionData, plansData });
            setSubscription(subscriptionData);
            setPlans(plansData);
          } catch (retryErr) {
            console.error('SubscriptionProvider: Retry also failed:', retryErr);
            setSubscription(null);
            setPlans([]);
          }
        }
      } else {
        console.warn('SubscriptionProvider: No token found, cannot fetch from backend');
        setSubscription(null);
        setPlans([]);
      }
      
      setLoading(false);
    }
    
    initializeSubscription();
  }, [isAuthenticated, user]);

  // Save subscription data to localStorage whenever it changes
  useEffect(() => {
    if (subscription !== null) {
      console.log('SubscriptionProvider: Saving subscription to localStorage:', subscription);
      localStorage.setItem('subscription', JSON.stringify(subscription));
    }
  }, [subscription]);

  useEffect(() => {
    if (plans.length > 0) {
      console.log('SubscriptionProvider: Saving plans to localStorage:', plans);
      localStorage.setItem('plans', JSON.stringify(plans));
    }
  }, [plans]);

  const updateSubscription = (newSubscription) => {
    setSubscription(newSubscription);
  };

  const refreshSubscription = async () => {
    setLoading(true);
    try {
      const result = await import('../api/cacheAfterLogin').then((mod) => mod.cacheAfterLogin());
      const subscriptionData = result.subscription || null;
      const plansData = result.plans || [];
      
      setSubscription(subscriptionData);
      setPlans(plansData);
    } catch (err) {
      console.error('SubscriptionProvider: Error refreshing subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    subscription: subscription || null,
    plans: plans || [],
    loading,
    updateSubscription,
    refreshSubscription
  };

  console.log('SubscriptionProvider: Current subscription:', subscription);
  console.log('SubscriptionProvider: Current plans:', plans);
  console.log('SubscriptionProvider: Rendering provider with children');

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};
